import React, { useState } from "react";
import AFSAimg from "../../../Assets/Images/login/login.png";
import popupImg from "../../../Assets/Images/login/login.png";
import {
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiMessageSquare,
  FiLoader,
} from "react-icons/fi";
import axiosInstance from "../../../utils/axios";
import Swal from "sweetalert2";

const PopUpForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\s/g, ""))) {
      newErrors.mobile = "Mobile number must be 10 digits";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await axiosInstance.post("/volunteer", formData);

      if (response) {
        setFormData({
          fullName: "",
          email: "",
          mobile: "",
          message: "",
        });

        Swal.fire({
          icon: "success",
          title: "Thank you!",
          text: "Your application has been submitted successfully. We'll get back to you soon!",
          confirmButtonColor: "#02323F",
        });

        onClose();
      } else {
        throw new Error("Failed to submit application");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4 py-6"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-xl flex flex-col md:flex-row overflow-hidden max-h-[90vh]">
        {/* Image Section */}
        <div className="hidden md:block w-full md:w-1/2">
          <img
            src={popupImg}
            alt="AFSA"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Form Section */}
        <div className="w-full md:w-1/2 px-6 sm:px-8 md:px-10 py-8 sm:py-12 flex flex-col justify-center overflow-y-auto relative">
          {/* Close Button - Top Right */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full transition-colors duration-200 z-10"
            disabled={isSubmitting}
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>

          {/* Header */}
          <div className="mb-6 mt-8">
            <h2 className="text-2xl font-sans text-center mb-1">
              JOIN AS VOLUNTEER
            </h2>
            <p className="text-center text-gray-500 text-sm">
              Fill in your details to join us as a volunteer.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name*
              </label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-2 text-sm border ${
                    errors.fullName ? "border-red-500" : "border-gray-300"
                  } rounded-md`}
                  placeholder="Full Name"
                  disabled={isSubmitting}
                />
              </div>
              {errors.fullName && (
                <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address*
              </label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-2 text-sm border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-md`}
                  placeholder="Email Address"
                  disabled={isSubmitting}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Mobile Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number*
              </label>
              <div className="relative">
                <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-2 text-sm border ${
                    errors.mobile ? "border-red-500" : "border-gray-300"
                  } rounded-md`}
                  placeholder="Mobile Number"
                  disabled={isSubmitting}
                />
              </div>
              {errors.mobile && (
                <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>
              )}
            </div>

            {/* Message Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message*
              </label>
              <div className="relative">
                <FiMessageSquare className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full pl-10 pr-4 py-2 text-sm border ${
                    errors.message ? "border-red-500" : "border-gray-300"
                  } rounded-md resize-none`}
                  placeholder="Tell us why you want to volunteer with us..."
                  disabled={isSubmitting}
                />
              </div>
              {errors.message && (
                <p className="text-xs text-red-500 mt-1">{errors.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-between gap-4 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="w-1/2 border border-gray-400 py-2 rounded-md text-sm hover:bg-gray-50 transition-colors duration-200"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-1/2 bg-[#0F172A] text-white py-2 rounded-md text-sm disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {isSubmitting && <FiLoader className="w-4 h-4 animate-spin" />}
                <span>{isSubmitting ? "Submitting..." : "Submit"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PopUpForm;
